import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ZoomIn, ZoomOut } from "lucide-react";

import "./OrgTree.css";
import { AppLayout, PageBackLink } from "../../../components/layout";
import sessionDB from "../../../utils/sessionDB";

import PersonCard, { Avatar } from "../components/PersonCard";
import PersonPopover from "../components/PersonPopover";
import { SearchIcon, TargetIcon, WarningIcon } from "../components/Icons";

import { fetchManagerChain, fetchDirectReports, fetchEmployeeDetails } from "../utils/orgApi";
import {
  createOrgIndex,
  mergeChain,
  mergeReports,
  getVisibleChain,
  searchEmployees } from
"../utils/orgTree";

// Absolute route path the Unassigned page lives at inside the /hrms/* area.
const UNASSIGNED_PATH = "/hrms-ess/org-chart/unassigned";

// =============================================================================
// OrgChartPage.jsx — the Org Chart screen, ported into workplane-ui as a
// module page. Renders inside the shared <AppLayout> (Header + Sidebar +
// Breadcrumb) instead of the standalone app's own Sidebar/Header.
//
// This file has these pieces, top to bottom:
//   1. ChartSearch     — the search box in the toolbar
//   2. TreeNode        — recursive card tree (boxes + connector lines), used
//                         at every breakpoint — mobile gets the same UI as
//                         desktop, scaled to fit via the zoom controls
//   3. ChartSkeleton   — loading placeholder
//   4. OrgChartPage (default export) — the actual page
//
// The real HRMS hierarchy endpoint (see utils/orgApi.js) only ever returns
// one employee's manager chain or one employee's full downward subtree at a
// time, so the tree is built up incrementally in a client-side cache
// (utils/orgTree.js) as the user re-centres or expands cards.
// =============================================================================

// ─── Type-ahead search — selecting a result re-centres the chart ─────────────
function ChartSearch({ index, onSelect }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const results = searchEmployees(index, query);

  const pick = (person) => {
    onSelect(person);
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="relative flex-1 min-w-[220px] max-w-[420px]" ref={boxRef}>
      <div className="flex items-center gap-2 bg-white border border-border rounded-lg px-3.5 py-2.5 text-slate-400 focus-within:border-accent">
        <SearchIcon />
        <input
          className="border-none outline-none text-sm text-foreground w-full bg-transparent placeholder:text-slate-400"
          placeholder={i18n.t("formActions.searchByNameOrDesignation")}
          value={query}
          onChange={(e) => {setQuery(e.target.value);setOpen(true);}}
          onFocus={() => setOpen(true)} />
        
      </div>

      {open && query.trim() &&
      <div className="absolute top-[calc(100%+6px)] left-0 right-0 bg-white border border-slate-200 rounded-[10px] shadow-[0_12px_28px_rgba(15,23,42,0.14)] z-[60] overflow-hidden max-h-[320px] overflow-y-auto">
          {results.length > 0 ?
        results.map((p) =>
        <button
          key={p.empId}
          className="flex items-center gap-2.5 w-full px-3.5 py-2.5 bg-transparent border-none cursor-pointer text-left hover:bg-slate-100"
          onClick={() => pick(p)}>
          
                <Avatar person={p} small />
                <span style={{ minWidth: 0 }}>
                  <p className="m-0 text-sm font-medium text-foreground">{p.name}</p>
                  <p className="m-0 text-xs text-muted-foreground">{p.designation} · {p.department}</p>
                </span>
              </button>
        ) :

        <div className="p-3.5 text-[13px] text-muted-foreground"><Trans i18nKey="orgCharts.noEmployeesMatch" />{query}"</div>
        }
        </div>
      }
    </div>);

}

// ─── Desktop tree node (recursive, elbow connectors via CSS) ─────────────────
function TreeNode({ id, index, expandedIds, onToggle, onOpen, focusId, meId, isRoot = false, showUp = false, onExpandUp, ancestry }) {
  const person = index.byId.get(id);
  if (!person) return null;

  const kids = index.childrenOf.get(id) ?? [];
  const expanded = expandedIds.has(id);
  const nextAncestry = new Set(ancestry);
  nextAncestry.add(id);

  return (
    <li className={`oc-li${isRoot ? " oc-li--root" : ""}`}>
      <PersonCard
        person={person}
        isFocus={id === focusId}
        isMe={id === meId}
        reporteeCount={kids.length}
        expanded={expanded}
        onToggleDown={() => onToggle(id)}
        showUp={showUp}
        onExpandUp={onExpandUp}
        onOpen={onOpen} />
      

      {expanded && kids.length > 0 &&
      <ul className="oc-children">
          {kids.map((kidId) =>
        ancestry.has(kidId) ?
        <li key={kidId} className="oc-li">
                <PersonCard
            person={index.byId.get(kidId)}
            warning
            isMe={kidId === meId}
            onOpen={onOpen} />
          
              </li> :

        <TreeNode
          key={kidId}
          id={kidId}
          index={index}
          expandedIds={expandedIds}
          onToggle={onToggle}
          onOpen={onOpen}
          focusId={focusId}
          meId={meId}
          ancestry={nextAncestry} />


        )}
        </ul>
      }
    </li>);

}

// ─── Skeleton — placeholder cards in the tree layout ─────────────────────────
function ChartSkeleton() {
  const card = <div className="w-[200px] h-[132px] rounded-xl bg-[#E8EEF5] animate-pulse" />;
  const vline = <div className="w-0.5 h-[26px] bg-border shrink-0" />;
  return (
    <div className="flex flex-col items-center min-w-max mx-auto" aria-hidden="true">
      {card}
      {vline}
      {card}
      {vline}
      <div className="flex gap-6">
        {card}
        {card}
        {card}
      </div>
    </div>);

}

// =============================================================================
// Page: Org Chart
// =============================================================================
export default function OrgChartPage() {
  const navigate = useNavigate();
  const emp = sessionDB.getEmpObj(); // the logged-in user's profile
  const meId = emp?.emp_id; // their empId — drives "opens centred on me"

  const [index, setIndex] = useState(() => createOrgIndex());
  const pendingReportFetches = useRef(new Set());

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [focusId, setFocusId] = useState(meId);
  const [upLevels, setUpLevels] = useState(1);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [loopEmpId, setLoopEmpId] = useState(null);
  const [popPerson, setPopPerson] = useState(null);
  const [zoom, setZoom] = useState(1);

  // Fetches id's manager chain + direct reports and centres the chart on
  // them — reused for initial load, "Back to me", and picking a search result.
  const loadFocus = async (id) => {
    const chainRecords = await fetchManagerChain(id);
    const reportRecords = await fetchDirectReports(id);
    const { hasLoop, loopEmpId: loopedAt } = mergeChain(index, chainRecords);
    mergeReports(index, id, reportRecords);
    setIndex({ byId: index.byId, childrenOf: index.childrenOf });
    setLoopEmpId(hasLoop ? loopedAt : null);
    setFocusId(id);
    setUpLevels(1);
    setExpandedIds(new Set([id]));
  };

  useEffect(() => {
    if (meId == null || meId === "") return;

    loadFocus(meId)
    .catch((err) => setError(`Failed to load the org chart: ${err.message}`))
    .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meId]);

  const recentre = (id) => {
    setLoading(true);
    setError(null);
    loadFocus(id)
    .catch((err) => setError(`Failed to load the org chart: ${err.message}`))
    .finally(() => setLoading(false));
  };

  const toggleDown = async (id) => {
    if (expandedIds.has(id)) {
      setExpandedIds((prev) => {const next = new Set(prev);next.delete(id);return next;});
      return;
    }
    setExpandedIds((prev) => new Set(prev).add(id));

    if (index.childrenOf.has(id) || pendingReportFetches.current.has(id)) return;
    pendingReportFetches.current.add(id);
    try {
      const reports = await fetchDirectReports(id);
      mergeReports(index, id, reports);
      setIndex({ byId: index.byId, childrenOf: index.childrenOf });
    } catch (err) {
      setError(`Failed to load reports: ${err.message}`);
      setExpandedIds((prev) => {const next = new Set(prev);next.delete(id);return next;});
    } finally {
      pendingReportFetches.current.delete(id);
    }
  };

  // Opens the popover immediately with whatever's already cached (fast,
  // no blank flash), then fetches the full employee record — this is the
  // only source for phone/email, which the hierarchy endpoint never
  // returns — and merges it in once it lands. If it fails, the popover
  // just keeps showing what the tree already had.
  const openPerson = (person) => {
    setPopPerson(person);
    fetchEmployeeDetails(person.empId)
    .then((details) => {
      if (!details) return;
      setPopPerson((prev) => prev && prev.empId === person.empId ? { ...prev, ...details } : prev);
    })
    .catch(() => {});
  };

  const expandUp = () => setUpLevels((n) => n + 1);

  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 1.5;
  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + 0.1) * 100) / 100));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - 0.1) * 100) / 100));

  const focus = index.byId.get(focusId) ?? null;

  const { chain, canExpandUp } = focus ?
  getVisibleChain(index, focusId, upLevels) :
  { chain: [], canExpandUp: false };

  // TODO: no company-wide "everyone with no manager" endpoint yet (see
  // orgApi.js) — the Unassigned chip/count stays hidden until one exists.
  const unassignedCount = 0;
  const isUnassignedPerson = () => false;

  const onlyEmployee = !loading && !error && focus && focus.managerId == null &&
  (index.childrenOf.get(focusId)?.length ?? 0) === 0;

  const topmostIsFocus = chain.length === 0;

  return (
    <AppLayout>
      <div className="mb-6">
        <PageBackLink to="/" />
        <h1 className="m-0 mb-1 text-xl md:text-[26px] font-bold text-foreground uppercase tracking-[0.03em]">
          <Trans i18nKey="orgCharts.orgChart" />
        </h1>
      </div>

      {/* ── Toolbar: search, back to me, unassigned bucket ── */}
      <div className="flex items-center gap-2 md:gap-3 flex-wrap mb-5 md:mb-7">
        <ChartSearch index={index} onSelect={(p) => recentre(p.empId)} />

        {focusId !== meId &&
        <button
          className="flex items-center gap-2 bg-white border border-border rounded-lg px-3 py-2 md:px-4 md:py-2.5 text-[13px] md:text-sm font-medium text-accent cursor-pointer whitespace-nowrap transition-colors hover:bg-slate-50"
          onClick={() => recentre(meId)}>
          
            <TargetIcon />
            <Trans i18nKey="formActions.backToMe" />
          </button>
        }

        {unassignedCount > 0 &&
        <button
          className="flex items-center gap-2 bg-[#FEF3C7] border border-[#FDE68A] rounded-full px-3 py-[7px] md:px-4 md:py-2 text-xs md:text-[13px] font-semibold text-[#92400E] cursor-pointer whitespace-nowrap transition-colors hover:bg-[#FDE68A]"
          onClick={() => navigate(UNASSIGNED_PATH)}>
          
            <WarningIcon size={13} />
            <Trans i18nKey="orgCharts.unassigned" />{unassignedCount})
          </button>
        }

        {/* Zoom controls — same card-tree UI renders on mobile now, so
             zooming out is how a narrow viewport fits more of the tree. */}
        <div className="flex items-center gap-0.5 bg-white border border-border rounded-lg px-1 py-1 ml-auto">
          <button
            type="button"
            className="flex items-center justify-center w-7 h-7 rounded-md text-slate-500 cursor-pointer transition-colors hover:bg-slate-100 hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            onClick={zoomOut}
            disabled={zoom <= ZOOM_MIN}
            aria-label={i18n.t("orgCharts.zoomOut")}>
            
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="w-10 text-center text-xs font-medium text-muted-foreground select-none">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            className="flex items-center justify-center w-7 h-7 rounded-md text-slate-500 cursor-pointer transition-colors hover:bg-slate-100 hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            onClick={zoomIn}
            disabled={zoom >= ZOOM_MAX}
            aria-label={i18n.t("orgCharts.zoomIn")}>
            
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>
      </div>

      {error &&
      <div className="bg-white border border-slate-200 rounded-[10px] px-5 py-4 text-sm text-[#EF4444] max-w-[560px]">
          {error}
        </div>
      }

      {onlyEmployee &&
      <div className="bg-white border border-slate-200 rounded-[10px] px-5 py-4 text-sm text-muted-foreground max-w-[560px]">
          <Trans i18nKey="orgCharts.noReportingRelationshipsExistYetYouAreThe" />
        </div>
      }

      {loading &&
      <div className="overflow-auto max-h-[70vh] px-1 pt-2 pb-10">
          <ChartSkeleton />
        </div>
      }

      {index && focus && (
      /* Same card tree at every breakpoint — bounded height + its own
         scrollbar (both axes) so drilling down/sideways scrolls this box
         instead of the whole page, and the zoom controls above let a
         narrow (mobile) viewport fit more of it without hunting for it
         via a completely different, cut-down mobile layout. */
      <div className="overflow-auto max-h-[70vh] px-1 pt-2 pb-10">
          <div className="flex flex-col items-center min-w-max mx-auto" style={{ zoom }}>
            {chain.map((m, i) =>
          <div key={m.empId} className="flex flex-col items-center">
                <PersonCard
              person={m}
              isMe={m.empId === meId}
              warning={m.empId === loopEmpId}
              showUp={i === 0 && canExpandUp}
              onExpandUp={expandUp}
              onOpen={openPerson} />
            
                <div className="w-0.5 h-[26px] bg-border shrink-0" />
              </div>
          )}

            <ul className="flex justify-center m-0 p-0">
              <TreeNode
              id={focusId}
              index={index}
              expandedIds={expandedIds}
              onToggle={toggleDown}
              onOpen={openPerson}
              focusId={focusId}
              meId={meId}
              isRoot
              showUp={topmostIsFocus && canExpandUp}
              onExpandUp={expandUp}
              ancestry={new Set()} />
            
            </ul>
          </div>
        </div>)
      }

      {/* Person Detail Popover — opened from any card on this page. */}
      {popPerson &&
      <PersonPopover
        person={popPerson}
        isUnassigned={isUnassignedPerson(popPerson)}
        onClose={() => setPopPerson(null)}
        onViewOrgChart={(p) => {setPopPerson(null);recentre(p.empId);}} />

      }
    </AppLayout>);

}
